import { getLocalizedMessage } from '../../../util/useLocalizedNotifications';
/**
 * deriveIndexingView — single source of truth for admin UI indexing presentation.
 *
 * A03 contract (install-indexing-orchestration / TASK-PACKS.md §A03):
 *   1. All inspected admin surfaces use bridge truth:
 *        - readiness.can_search (authoritative "ready" signal)
 *        - indexing.status (raw backend state)
 *        - job_id, progress, counts
 *   2. State mapping is consistent across surfaces:
 *        - idle / no index     → "needs start"
 *        - queued              → "waiting / starting / draining"
 *        - running             → "in progress"
 *        - completed + can_search=true → "ready"
 *        - error               → "retry needed"
 *   3. The UI must NOT imply that installing the module alone makes search
 *      ready. AC3: a `completed` payload from the bridge is NOT enough to
 *      render "Ready" — `readinessCanSearch === true` is the ONLY path
 *      to `viewStatus: 'ready'`.
 *
 * Pure function, no side effects. Safe to call from any consumer.
 *
 * @param {{
 *   rawStatus?: ('idle' | 'queued' | 'running' | 'completed' | 'error' | 'failed' | 'done' | null | undefined),
 *   readinessCanSearch?: (boolean | null | undefined),
 *   jobId?: (string | null | undefined),
 *   error?: (string | null | undefined),
 *   progress?: (number | null | undefined),
 *   indexedCount?: (number | null | undefined),
 *   totalCount?: (number | null | undefined),
 * }} input
 * @returns {{
 *   viewStatus: ('idle' | 'draining' | 'in_progress' | 'ready' | 'failed'),
 *   // - 'idle':       rawStatus is null/undefined/'idle' (no job started or
 *                     bridge has not replied)
 *   // - 'draining':   rawStatus is 'queued' (job is queued or completed but
 *                     readinessCanSearch is not yet true — the AC3
 *                     "Finalizing…" window). 'draining' NEVER collapses to
 *                     'ready', even on race-window readinessCanSearch=true.
 *   // - 'in_progress': rawStatus is 'running' (job is actively indexing)
 *   // - 'ready':      rawStatus is 'completed'/'done' AND readinessCanSearch
 *                     is true (the ONLY path to 'ready' — AC3)
 *   // - 'failed':     rawStatus is 'error'/'failed', OR a truthy `error`
 *                     field is supplied. There is no separate 'error' state:
 *                     the input enum 'error' | 'failed' collapses to a single
 *                     'failed' viewStatus, since the operator-facing meaning
 *                     is identical ("retry needed").
 *   label: string,
 *   showRetry: boolean,
 *   showStart: boolean,
 *   showSpinner: boolean,
 *   readinessIsAuthoritative: boolean,
 * }}
 */
export function deriveIndexingView(input = {}, localizeMessage = getLocalizedMessage) {
  const rawStatus = input.rawStatus ?? null;
  const readinessCanSearch = input.readinessCanSearch === true;
  const hasError = !!input.error;

  // ── null / undefined rawStatus → bridge has not replied yet ───────────
  if (rawStatus == null) {
    return {
      viewStatus: 'idle',
      label: localizeMessage('common.loading'),
      showRetry: false,
      showStart: false,
      showSpinner: true,
      readinessIsAuthoritative: false,
    };
  }

  // ── error state ────────────────────────────────────────────────────────
  if (rawStatus === 'error' || rawStatus === 'failed' || hasError) {
    return {
      viewStatus: 'failed',
      label: localizeMessage('ui.integration.shared.deriveIndexingView.indexingFailed'),
      showRetry: true,
      showStart: false,
      showSpinner: false,
      readinessIsAuthoritative: false,
    };
  }

  // ── idle / no index → needs start ─────────────────────────────────────
  if (rawStatus === 'idle') {
    return {
      viewStatus: 'idle',
      label: localizeMessage('ui.integration.shared.deriveIndexingView.searchNotSetUp'),
      showRetry: false,
      showStart: true,
      showSpinner: false,
      readinessIsAuthoritative: false,
    };
  }

  // ── queued with a job id → draining / starting ────────────────────────
  // A03 AC3: a queued job is NEVER "ready" even if the bridge happened
  // to also report readinessCanSearch=true (race window between
  // bridge finalization and index visibility).
  if (rawStatus === 'queued') {
    return {
      viewStatus: 'draining',
      label: localizeMessage('ui.integration.shared.deriveIndexingView.starting'),
      showRetry: false,
      showStart: false,
      showSpinner: true,
      readinessIsAuthoritative: false,
    };
  }

  // ── running → in progress ─────────────────────────────────────────────
  if (rawStatus === 'running') {
    return {
      viewStatus: 'in_progress',
      label: localizeMessage('ui.integration.shared.deriveIndexingView.indexing'),
      showRetry: false,
      showStart: false,
      showSpinner: true,
      readinessIsAuthoritative: false,
    };
  }

  // ── completed (raw) ───────────────────────────────────────────────────
  // A03 AC3: completed is ready ONLY when readinessCanSearch === true.
  // The bridge reports can_search=true only after the index is actually
  // usable. A "completed" payload without can_search is the brief
  // final-install window where the worker has finished the job but
  // the index is not yet searchable — we must NOT show "Ready" there.
  if (rawStatus === 'completed' || rawStatus === 'done') {
    if (readinessCanSearch) {
      return {
        viewStatus: 'ready',
        label: localizeMessage('ui.integration.shared.deriveIndexingView.ready'),
        showRetry: false,
        showStart: false,
        showSpinner: false,
        readinessIsAuthoritative: true,
      };
    }
    return {
      viewStatus: 'draining',
      label: localizeMessage('ui.integration.shared.deriveIndexingView.finalizing'),
      showRetry: false,
      showStart: false,
      showSpinner: true,
      readinessIsAuthoritative: false,
    };
  }

  // ── unknown raw status → treat as idle (loading) ──────────────────────
  return {
    viewStatus: 'idle',
    label: localizeMessage('common.loading'),
    showRetry: false,
    showStart: false,
    showSpinner: true,
    readinessIsAuthoritative: false,
  };
}

/**
 * Map deriveIndexingView output to a minimal boolean: is the index ready
 * for the merchant to use? This is the AC3-defensive "ready" check
 * that should replace any `status === 'completed' || status === 'done'`
 * comparison in the UI.
 */
export function isIndexingViewReady(input) {
  return deriveIndexingView(input).viewStatus === 'ready';
}
